// Local Imports
import {
  AddExperienceIcon,
  CertificationsIcon,
  EditProfileIcon,
  EducationIcon,
  LicensesIcon,
  ProfileIcon,
  RecommendationIcon,
  SkillsIcon,
  StarIcon,
  VerifiedIcon,
} from "../../../assets/icons";
import { ProfileCard } from "../../../components/shared";
import { useNavigate } from "react-router-dom";
import { useProfileCompletion } from "../../../lib/react-query/profile";
import OnboardingChecklist from "../../../components/Dashboard/OnboardingChecklist";

const Profile = () => {
  const navigate = useNavigate();
  const { data: profileCompletionData } = useProfileCompletion();
  const profileCompletion = profileCompletionData?.percentage || 0;

  // Get completion items for each section (weight is 0-100 completion percentage)
  const completionItems = profileCompletionData?.items || {};
  const getCompletion = (key) => completionItems[key]?.weight || 0;

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="bg-white rounded-xl shadow-sm border border-surface-border p-4">
        <div className="font-sans vertical-center gap-4">
          <ProfileIcon width={32} height={32} />
          <div>
            <h3 className="text-xl font-semibold text-grey">Profile Hub</h3>
            <p className="text-mediumgrey text-sm">
              Manage the sections of your profile
            </p>
          </div>
        </div>
      </div>

      {profileCompletion <= 90 && (
        <OnboardingChecklist profileCompletion={profileCompletion} />
      )}

      {/* Personal */}
      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-mediumgrey uppercase tracking-wider">
          Personal
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <ProfileCard
            icon={<EditProfileIcon color="#1E009F" />}
            iconSX="bg-secondary"
            title="Edit Personal Details"
            text="Update your profile photo, contact info, and bio"
            onClick={() => navigate("/profile/edit")}
          />
          <ProfileCard
            icon={<SkillsIcon color="#1E009F" />}
            iconSX="bg-primary/10"
            title="Skills"
            text="Showcase your professional skills and expertise"
            range={getCompletion("skills")}
            showProgress={true}
            onClick={() => navigate("/profile/skills")}
          />
          <ProfileCard
            icon={<VerifiedIcon color="#1E009F" />}
            iconSX="bg-primary/10"
            title="Identity Verification"
            text="Verify your identity to build trust with connections"
            range={getCompletion("identity_verification")}
            showProgress={true}
            onClick={() => navigate("/profile/profile-details")}
          />
        </div>
      </section>

      {/* Professional History */}
      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-mediumgrey uppercase tracking-wider">
          Professional History
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <ProfileCard
            icon={<AddExperienceIcon color="black" />}
            iconSX="bg-black/10"
            title="Experience"
            text="Add job experiences and roles held"
            range={getCompletion("experiences")}
            showProgress={true}
            onClick={() => navigate("/profile/experience")}
          />
          <ProfileCard
            icon={<EducationIcon color="#34A853" />}
            iconSX={"bg-success/10"}
            title={"Education"}
            text={"Add educational qualifications and degrees"}
            range={getCompletion("education")}
            showProgress={true}
            onClick={() => navigate("/profile/education")}
          />
        </div>
      </section>

      {/* Credentials */}
      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-mediumgrey uppercase tracking-wider">
          Credentials
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <ProfileCard
            icon={<CertificationsIcon color="#2964E2" />}
            iconSX="bg-black/10"
            title="Certifications"
            text="Add professional certifications earned"
            range={getCompletion("certifications")}
            showProgress={true}
            onClick={() => navigate("/profile/certifications")}
          />
          <ProfileCard
            icon={<LicensesIcon color="#D89B00" />}
            iconSX="bg-secondary/10"
            title="Licenses"
            text="Add licenses related to your field"
            range={getCompletion("licenses")}
            showProgress={true}
            onClick={() => navigate("/profile/licenses")}
          />
        </div>
      </section>

      {/* Social Proof */}
      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-mediumgrey uppercase tracking-wider">
          Social Proof
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <ProfileCard
            icon={<RecommendationIcon color="#9F0086" />}
            iconSX="bg-secondary/10"
            title="Recommendations"
            text="View professional recommendations received"
            range={getCompletion("recommendations")}
            showProgress={true}
            onClick={() => navigate("/profile/recommendations")}
          />
          <ProfileCard
            icon={<StarIcon color="#FFD700" />}
            iconSX="bg-secondary/10"
            title="Reviews"
            text="Manage your sent and received reviews"
            onClick={() => navigate("/profile/reviews")}
          />
        </div>
      </section>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => navigate("/account-settings")}
          className="text-sm text-mediumgrey hover:text-primary transition-colors duration-200 flex flex-col items-center gap-1 group text-center"
        >
          <span>Looking for password or notification settings?</span>
          <span className="text-primary group-hover:underline whitespace-nowrap">→ Account Settings</span>
        </button>
      </div>
    </div>
  );
};

export default Profile;
